import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary: gradient brand (azul → turquesa). Para acciones positivas principales.
        default: 'bg-gradient-to-r from-brand-primary to-brand-accent text-white shadow-soft hover:shadow-elevated hover:-translate-y-px active:translate-y-0',
        primary: 'bg-gradient-to-r from-brand-primary to-brand-accent text-white shadow-soft hover:shadow-elevated hover:-translate-y-px active:translate-y-0',
        // CTA: verde energía. Para "Empezar", "Reservar", "Suscribirme" — la acción más deseada.
        cta: 'bg-brand-energy text-white shadow-soft hover:bg-brand-energy-light hover:shadow-elevated hover:-translate-y-px',
        // Secondary: outline sólido turquesa
        secondary: 'bg-white border border-brand-accent/30 text-brand-primary hover:bg-brand-accent/5 hover:border-brand-accent',
        // Outline: borde gris suave
        outline: 'border border-border bg-background text-foreground hover:bg-muted hover:border-brand-accent/50',
        // Ghost: sin fondo
        ghost: 'text-foreground hover:bg-muted',
        // Link: solo texto subrayado
        link: 'text-brand-accent underline-offset-4 hover:underline p-0 h-auto',
        // Destructive: rojo
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Botón blanco para fondos oscuros — texto SIEMPRE oscuro
        'on-dark': 'bg-white text-[#10232B] hover:bg-white/90 hover:text-[#10232B] shadow-soft font-medium',
        // Outline blanco transparente sobre fondos oscuros — texto SIEMPRE blanco
        'on-dark-outline': 'border border-white/25 bg-white/5 text-white hover:bg-white/10 hover:border-white/45 hover:text-white backdrop-blur-sm',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-8 text-base',
        xl: 'h-14 rounded-xl px-10 text-base font-semibold',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
